import {
  ExpenseType,
  WorkOrderAssignmentStatus,
  WorkOrderJobOpeningOfferStatus,
  WorkOrderJobOpeningStatus,
  WorkOrderStatus,
} from "@/gql/graphql";

export const workOrderMessages = {
  // Breadcrumb
  breadcrumb: {
    proDrivers: "Pro Drivers",
    workOrders: "Work orders",
  },

  // Tabs
  tabs: {
    details: "Details",
    quotes: "Quotes",
    invoices: "Invoices",
    billing: "Billing",
    findDrivers: "Job Openings",
    messages: "Messages",
    activity: "Activity",
    incidents: "Incidents",
  },

  // Flowchart
  flow: {
    request: "Request",
    quote: "Quote",
    invoice: "Invoice",
    driver: "Driver {number}",
    pickedUp: "Picked Up",
    droppedOff: "Dropped Off",
    paid: "Paid",
  },

  // Cards
  cards: {
    quote: {
      title: "Quote",
      noQuoteYet: "No quote created yet",
      createQuote: "Create Quote",
      decline: "Decline",
      accept: "Accept",
      previewPdf: "View PDF",
      reference: "Reference",
      purchaseOrderNumber: "Purchase Order Number",
      quoteDate: "Quote Date",
      expiryDate: "Expiry Date",
      amountExcTax: "Amount (ex tax)",
      amountIncTax: "Amount (inc tax)",
    },
    invoice: {
      title: "Invoice",
      noInvoiceYet: "No invoice created yet",
      createInvoice: "Create Invoice",
      creating: "Creating...",
      issue: "Issue",
      payNow: "Pay Now",
      previewPdf: "View PDF",
      reference: "Reference",
      invoiceDate: "Invoice Date",
      dueDate: "Due Date",
      amountExcTax: "Amount (ex tax)",
      amountIncTax: "Amount (inc tax)",
      assignExisting: "Assign existing invoice",
      notRequired: "Invoice not required",
      notRequiredDescription:
        "This work order will complete once all drivers are done, without an invoice",
      requireInvoice: "Require invoice",
    },
    driver: {
      title: "Driver {number}",
      noDriverYet: "No driver assigned yet",
      addDriver: "Add Driver",
      approveReceipts: "Approve Receipts",
      markCompleted: "Mark Completed",
      payAndComplete: "Payout & Complete",
      yearsOld: "{age} years old",
      pickup: "Pickup",
      pickedUp: "Picked Up",
      delivery: "Delivery",
      delivered: "Delivered",
      supplierRef: "Supplier Ref",
      bookingRef: "Booking Ref",
      agreement: "Agreement",
      noAgreement: "No agreement yet",
      driverNotes: "Driver Notes",
      noMovementDetails: "No movement details added yet.",
      reportIncident: "Report incident",
      reportingWindowClosed:
        "Reporting window closed — email prodrivers@imoova.com",
    },
  },

  // Warnings
  warnings: {
    noEmailsAttached: "No emails are attached to the work order.",
    addAnEmail: "Add an email",
    serviceCenterPickup:
      "This pickup is at a service center. Please make sure the driver has the necessary documents available for pickup. These could include the VIN, proof of purchase or ownership, and your authorisation to collect the vehicle, otherwise the service center may not release it.",
  },

  // Actions
  actions: {
    actions: "Actions",
    archive: "Archive",
    archiveTitle: "Archive Work Order",
    archiveMessage:
      "Do you want to archive work order? It will void any quotes and unpaid invoices attached to the work order",
    viewManifest: "View Manifest",
  },

  // Assignment actions
  assignmentActions: {
    actions: "Actions",
    editAssignment: "Edit Assignment",
    ratePerformance: "Rate Performance",
    editDriver: "Edit Driver",
    delete: "Delete",
    driverDeleted: "Driver deleted",
    uploadFiles: "Upload Files",
  },

  // Table & navigation
  workOrder: "Work Order",
  supplier: "Supplier",
  status: {
    [WorkOrderStatus.Archived]: "Archived",
    [WorkOrderStatus.Invoiced]: "Invoiced",
    [WorkOrderStatus.Completed]: "Completed",
    [WorkOrderStatus.QuoteIssued]: "Quoted",
    [WorkOrderStatus.QuoteRejected]: "Quote Rejected",
    [WorkOrderStatus.QuoteRequested]: "Requested",
    [WorkOrderStatus.AssigningDrivers]: "Assigning",
    [WorkOrderStatus.DriversAssigned]: "Assigned",
    [WorkOrderStatus.InProgress]: "In Progress",
  } satisfies {
    [key in WorkOrderStatus]: string;
  },
  vehicle: "Vehicle",
  from: "From",
  to: "To",
  created: "Created",
  requestQuote: "Request Quote",

  // Filters
  filters: {
    status: "Status",
    proDriver: "Pro driver",
  },

  // Header navigation
  navigation: {
    inProgress: "In Progress",
    completed: "Completed",
    cancelled: "Cancelled",
    all: "All",
  },

  // Dialog
  dialog: {
    requestQuote: "Request Quote",
    requestProDriver: "Request Pro Driver",
  },

  // Dialogs
  dialogs: {
    assignInvoice: {
      title: "Assign Invoice",
      description: "Select an existing invoice to assign to this work order.",
      invoiceLabel: "Invoice",
      submit: "Assign Invoice",
    },
  },

  // Progress indicator
  progress: {
    status: "Status",
    progress: "Progress",
    quote: "Quote",
    driversAssigned: "Drivers Assigned",
    inProgress: "In Progress",
    completed: "Completed",
  },

  // Status messages
  statusMessages: {
    archived: "This work order has been archived.",
    quoteRequested: {
      title: "Quote request submitted",
      description: "You will receive a response within 1 business day.",
      adminAction: "Create Quote",
    },
    quoteIssued: {
      notFound: "Current Quote Not Found",
      reject: "Reject",
      accept: "Accept Quote",
    },
    quoteAccepted: {
      title: "Quote Accepted!",
      description:
        "We're reaching out to our network of trusted drivers to find the best match for your vehicle transport.",
      followUp:
        "We'll be in contact soon with driver details and updated timing information.",
      viewQuote: "View Quote",
      noQuote: "There is no current quote",
      addDriver: "Add Driver",
    },
    driversAssigned: {
      title: "Drivers Assigned!",
      description:
        "Drivers have been assigned to this job. Preparing to start work.",
    },
    inProgress: {
      title: "In Progress!",
      description: "The driver is currently moving the vehicle",
      comingSoon: "🚧 Coming soon... 🚧",
      completeAndInvoice: "Complete & Invoice",
      invoiceCreated: "Invoice Created",
    },
    invoiced: {
      addInvoice: "Add Invoice",
      todo: "TODO",
    },
    quoteRejected: {
      archive: "Archive",
      issueNewQuote: "Issue New Quote",
    },
  },

  // Expense types
  expenseType: {
    [ExpenseType.DriverPay]: "Driver Pay",
    [ExpenseType.Fuel]: "Fuel",
    [ExpenseType.Ferry]: "Ferry",
    [ExpenseType.Flight]: "Flight",
    [ExpenseType.Transportation]: "Transportation",
    [ExpenseType.Toll]: "Toll",
    [ExpenseType.Accommodation]: "Accommodation",
    [ExpenseType.Other]: "Other",
  } satisfies { [key in ExpenseType]: string },

  // Price guide
  priceGuide: {
    title: "Price guide",
    vehicleType: "Vehicle type",
    departureCity: "Departure city",
    deliveryCity: "Departure city",
  },

  // Work order form
  form: {
    steps: {
      vehicles: "Vehicles",
      trip: "Pickup & delivery",
      schedule: "Dates & notes",
      back: "Back",
      next: "Next",
    },
    sections: {
      supplierContact: "Supplier & Contact",
      vehicleRequirements: "Vehicle & Driver Requirements",
      pickupDelivery: "Pickup & Delivery",
      additionalInfo: "Additional Information",
      notifications: "Notifications",
    },
    fields: {
      supplier: "Supplier",
      vehicleType: "Vehicle Type",
      numberOfDrivers: "Vehicles to move",
      pickupAddress: "Pickup Address",
      departAfter: "Depart After",
      deliveryAddress: "Delivery Address",
      deliverBefore: "Deliver Before",
      notesInstructions: "Notes & Special Instructions",
      placeholder:
        "Please specify any relevant information regarding the vehicle, special handling requirements, access instructions, or where it's located...",
      hasReturnTrip: "Return Trip",
      isServiceCenter: "Service Center",
      isServiceCenterTooltip:
        "Turn this on when the vehicle is collected from a dealership, workshop or other service center rather than your own depot. Service centers often won't release a vehicle to our driver without the VIN, proof of purchase or ownership, and your authorisation.",
    },
    serviceCenterDetected:
      "It looks like this pickup may be a service center. If it isn't, please untick the Service Center option.",
  },

  // Details card
  details: {
    pickUpLocation: "Pick Up Location",
    dropOffLocation: "Drop Off Location",
    vehicleType: "Vehicle Type",
    driversRequired: "Drivers Required",
    additionalInfo: "Additional Information",
    adminNotes: "Admin Notes",
    addAdminNotes: "Add admin notes...",
    noAdminNotes: "No admin notes",
    noSimilarWorkOrderNotes: "No notes on similar routes yet",
    showSimilarWorkOrderNotes: "Show notes from similar work orders",
    hideSimilarWorkOrderNotes: "Hide notes from similar work orders",
    clickEditToAddNotes: "Click edit to add notes",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving...",
    notifications: "Notifications",
    notificationsTo: "To",
    notificationsCc: "Cc",
    notificationsBcc: "Bcc",
    noNotificationRecipients: "None",
    clickEditToAddRecipients: "Click edit to add email recipients",
    manageNotifications: "Manage notification recipients",
    assignees: "Assignees",
    assignee: "Assignee",
    noAssignees: "Unassigned",
    assign: "Assign",
    assignAdmin: "Assign an admin",
    assignAdminSubtitle: "Put an Imoova staff member on this work order",
    assignAdminFailed: "Failed to assign",
    viewReturnOptions: "View return options on Rome2Rio",
    returnTrip: "Return Trip",
    hasReturnTrip: "This work order includes a return trip.",
  },

  // Drivers card
  drivers: {
    assignedDrivers: "Assigned Drivers",
    noDriversAssigned: "No drivers assigned",
    addDrivers: "Add drivers",
    addDriver: "Add Driver",
    ratePerformance: "Rate performance:",
    reviews: "Reviews",
    reviewDriver: "Review driver",
    drive: "drive",
    drives: "drives",
    drivesCount: "{count, plural, one {# drive} other {# drives}}",
    assignmentsCount:
      "{count, plural, one {# assignment} other {# assignments}}",
    userDeleted: "USER HAS BEEN DELETED",
    addDateOfBirth: "Add date of birth",
    files: "Files",
  },

  // Drivers screen
  driversScreen: {
    title: "Drivers",
    searchCenter: "Search Center",
    lookingForDrivers: "Looking for drivers within {radius} km",
    searchRadius: "Search Radius",
    missingDepartureCoords:
      "Departure address has no geocoordinates — drivers are not filtered by distance.",
    missingDeliveryCoords:
      "Delivery address has no geocoordinates — drivers are not filtered by distance.",
    departure: "Departure",
    delivery: "Delivery",
    searchFrom: "Search from",
  },

  // Assignments table
  assignment: {
    driver: "Driver",
    statusLabel: "Status",
    workOrder: "Work Order",
    route: "Route",
    pickup: "Pickup",
    delivery: "Delivery",
    supplier: "Supplier",
    status: {
      [WorkOrderAssignmentStatus.Assigned]: "Assigned",
      [WorkOrderAssignmentStatus.OnRoad]: "On Road",
      [WorkOrderAssignmentStatus.Delivered]: "Delivered",
      [WorkOrderAssignmentStatus.ReceiptsUploaded]: "Receipts Uploaded",
      [WorkOrderAssignmentStatus.ReceiptsApproved]: "Receipts Approved",
      [WorkOrderAssignmentStatus.Completed]: "Completed",
    } satisfies { [key in WorkOrderAssignmentStatus]: string },
  },

  // Job Openings
  jobOpenings: {
    title: "Job Openings",
    createJobOpening: "Create Job Opening",
    noJobOpenings: "No Job Openings",
    noJobOpeningsYet: "No job openings yet",
    createToNotify: "Create a job opening to notify drivers",
    position: "position",
    positions: "positions",
    offersSent: "{count, plural, one {# offer sent} other {# offers sent}}",
    depart: "Depart",
    deliver: "Deliver",

    // Create dialog
    createDialog: {
      title: "Create Job Opening",
      subtitle: "Create a job opening to notify drivers about this work",
      description: "Description",
      descriptionRequired: "Description is required",
      positionsAvailable: "Positions Available",
      positionsRequired: "At least 1 position is required",
      departAt: "Depart At",
      deliverAt: "Deliver At",
      selectDrivers: "Select Drivers (optional)",
      driversSelected: "{count} driver(s) selected",
      inviteAllDrivers: "Invite {count} driver(s)",
      selectSpecificDrivers: "Select drivers",
    },

    // Edit dialog
    editDialog: {
      title: "Edit Job Opening",
      subtitle: "Update the job opening details",
      save: "Save Changes",
    },

    // Notify drivers dialog
    notifyDialog: {
      title: "Notify Drivers",
      subtitle: "Notify {count} driver(s) about job openings",
      addJobOpening: "Add Job Opening",
      cancel: "Cancel",
      notifying: "Notifying...",
      notifyDrivers: "Notify Drivers",
      driversNotified: "Drivers notified",
    },

    // Offers
    offersTable: {
      noDrivers: "No drivers have accepted yet",
      showMore: "Show {count} more",
      showLess: "Show less",
      loading: "Loading…",
    },

    // Job opening status
    jobOpeningStatus: {
      [WorkOrderJobOpeningStatus.Open]: "Open",
      [WorkOrderJobOpeningStatus.Closed]: "Closed",
    } satisfies { [key in WorkOrderJobOpeningStatus]: string },

    // Offer status
    offerStatus: {
      [WorkOrderJobOpeningOfferStatus.Confirmed]: "Confirmed",
      [WorkOrderJobOpeningOfferStatus.DriverAccepted]: "Driver Accepted",
      [WorkOrderJobOpeningOfferStatus.Pending]: "Pending",
      [WorkOrderJobOpeningOfferStatus.Declined]: "Declined",
    } satisfies { [key in WorkOrderJobOpeningOfferStatus]: string },

    // Offer actions
    offerActions: {
      actions: "Actions",
      accept: "Accept",
      assign: "Assign",
      confirm: "Confirm",
      driverDeclined: "Driver Declined",
    },

    // Send to drivers
    sendToDrivers: {
      button: "Invite More Drivers",
      title: "Invite More Drivers",
      subtitle: "Select drivers to send this job opening to",
      send: "Send to Selected Drivers",
      sending: "Sending...",
      success: "Offers sent successfully",
    },

    // Send notifications
    sendNotifications: {
      button: "Resend Notifications",
      confirmTitle: "Resend Push Notifications",
      confirmMessage:
        "Are you sure you want to send a push notification to all drivers with offers for this job opening?",
      cancel: "Cancel",
      send: "Resend Notifications",
      sending: "Sending...",
      success: "Notifications sent successfully",
    },
  },
};
